<template>
  <section
    v-if="hasContent"
    class="ca-promo-module"
    :class="moduleClasses"
    :aria-labelledby="titleId"
    :aria-busy="content.loading || undefined"
  >
    <div
      class="ca-bullet-container ca-promo-module__container ca-container-fluid"
    >
      <div class="ca-promo-module__card ca-row">
        <div class="ca-promo-module__message ca-col-12 ca-col-md-6">
          <CaModuleMessage
            :id="titleId"
            class="ca-promo-module__module-message"
            :label="content.label"
            :title="content.title"
            :title-heading-type="content.titleHeadingType"
            :description="content.description"
            :description-heading-type="content.descriptionHeadingType"
            size="M"
            :theme="content.contentTheme"
          />

          <div
            v-if="content.actions.length"
            class="ca-promo-module__actions"
            :class="{
              'ca-promo-module__actions--equal': content.actions.length === 2,
            }"
          >
            <BaseButton
              v-for="(action, index) in content.actions"
              :key="action.id ?? index"
              class="ca-promo-module__action"
              :class="[action.buttonType || defaultButtonType, action.variant]"
              type="button"
              :label="action.label"
              :disabled="content.loading || action.disabled"
              @click="handleAction(action, index)"
            >
              <!-- Atencao: no BaseButton do DS os nomes dos slots estao trocados
                   face a posicao real — `trailingIcon` renderiza ANTES do label
                   (à esquerda) e `leadingIcon` DEPOIS (à direita). Os wrappers do
                   DS ja trazem o espacamento (mr-size-3 / ml-size-3).
                   O icone e decorativo: o label do botao ja carrega o significado. -->
              <template
                v-if="action.iconUrl && action.iconPosition === 'left'"
                #trailingIcon
              >
                <img
                  class="ca-promo-module__action-icon"
                  :src="action.iconUrl"
                  alt=""
                  aria-hidden="true"
                />
              </template>
              <template
                v-if="action.iconUrl && action.iconPosition === 'right'"
                #leadingIcon
              >
                <img
                  class="ca-promo-module__action-icon"
                  :src="action.iconUrl"
                  alt=""
                  aria-hidden="true"
                />
              </template>
            </BaseButton>
          </div>
        </div>

        <div class="ca-col-12 ca-col-md-6 ca-promo-module__media-parent">
          <CaPromoMedia
            class="ca-promo-module__media"
            :src="content.media.imageUrl"
            :src-set="content.media.srcSet"
            :sizes="content.media.sizes"
            :alt="content.media.alt"
            :cards="content.media.cards"
            :statistics="content.media.statistics"
            :related-content-label="content.media.relatedContentLabel"
            :new-window-label="content.media.newWindowLabel"
            :video="content.media.video"
            :loading="content.loading"
            @card-click="emit('card-click', $event)"
            @image-error="emit('image-error')"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, useId } from "vue";
import { BaseButton } from "ca-online-design-system";
import CaModuleMessage from "../../base/moduleMessage/CaModuleMessage.vue";
import CaPromoMedia from "./CaPromoMedia.vue";

defineOptions({
  name: "CaPromoModule",
});

const props = defineProps({
  model: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(["action-click", "card-click", "image-error"]);
const titleId = `ca-promo-module-${useId()}`;

const content = computed(() => {
  const source = props.model ?? {};
  const media = source.media ?? {};
  const desktopMediaPosition = normalizeOption(
    source.desktopMediaPosition,
    ["left", "right"],
    "right",
  );
  const stackedMediaPosition = source.stackedMediaPosition;
  const containerColorValue = readText(source.containerColor);
  const containerColor = containerColorValue
    ? normalizeOption(
        containerColorValue,
        ["light-green", "dark-green", "gray", "none"],
        "light-green",
      )
    : "none";
  const contentTheme = normalizeOption(
    source.contentTheme,
    ["default", "inverted"],
    "default",
  );
  const size = normalizeOption(source.size, ["s", "m"], "s");
  const title = readText(source.title);
  const sectionLabel =
    readText(source.sectionLabel) ||
    title ||
    readText(source.label) ||
    "Conteúdo promocional";
  const statistics = Array.isArray(media.contentCards)
    ? media.contentCards
    : [];
  const cardsEnabled =
    media.cards === undefined
      ? statistics.length > 0
      : normalizeBoolean(media.cards);
  const actions = Array.isArray(source.actions) ? source.actions : [];

  return {
    label: readText(source.label),
    title,
    titleHeadingType: source.titleHeadingType || "H2",
    sectionLabel,
    description: readText(source.description),
    descriptionHeadingType: source.descriptionHeadingType || "Paragraph",
    containerColor,
    contentTheme,
    size,
    desktopMediaPosition,
    stackedMediaPosition,
    loading: normalizeBoolean(source.loading),
    media: {
      imageUrl: readText(media.imageUrl),
      srcSet: readText(media.srcSet),
      sizes: readText(media.sizes) || createImageSizes(size),
      alt: readText(media.alt),
      cards: cardsEnabled,
      statistics,
      video: normalizeBoolean(media.video),
      relatedContentLabel:
        readText(media.relatedContentLabel) || "Conteúdos relacionados",
      newWindowLabel: readText(media.newWindowLabel) || "Abre numa nova janela",
    },
    actions: actions
      .slice(0, 2)
      .map((action, index) => ({
        id: action.id,
        label: readText(action.label),
        url: readText(action.link?.url),
        target: readText(action.link?.target) || "_self",
        disabled: normalizeBoolean(action.disabled),
        variant: index === 0 ? "primary" : "secondary",
        // Vazio = automatico: cai no defaultButtonType, derivado do Content Theme.
        buttonType: normalizeOption(
          action.buttonType,
          ["theme", "inverted", "danger", "warning"],
          "",
        ),
        iconUrl: readText(action.iconUrl),
        iconPosition: normalizeOption(
          action.iconPosition,
          ["left", "right"],
          "right",
        ),
      }))
      .filter((action) => action.label),
  };
});

const hasContent = computed(
  () =>
    Boolean(props.model) &&
    (content.value.loading ||
      content.value.label ||
      content.value.title ||
      content.value.description ||
      content.value.media.imageUrl ||
      content.value.media.statistics.length ||
      content.value.actions.length),
);

const moduleClasses = computed(() => [
  `ca-promo-module--container-${content.value.containerColor}`,
  `ca-promo-module--theme-${content.value.contentTheme}`,
  `ca-promo-module--size-${content.value.size}`,
  `ca-promo-module--desktop-${content.value.desktopMediaPosition}`,
  `ca-promo-module--stacked-${content.value.stackedMediaPosition}`,
  { "is-loading": content.value.loading },
]);

/** Estilo usado quando o autor nao escolheu nenhum no Sitecore. */
const defaultButtonType = computed(() =>
  content.value.contentTheme === "inverted" ? "inverted" : "theme",
);

function readText(value) {
  if (value && typeof value === "object") {
    const resolvedValue = value.text ?? value.label ?? value.value;
    return typeof resolvedValue === "object"
      ? readText(resolvedValue)
      : String(resolvedValue ?? "");
  }

  return String(value ?? "");
}

function normalizeOption(value, options, fallback) {
  const normalizedValue = readText(value);
  return options.includes(normalizedValue) ? normalizedValue : fallback;
}

function normalizeBoolean(value) {
  return value === true;
}

function createImageSizes(size) {
  const desktopWidth = size === "s" ? "410px" : "628px";

  return `(min-width: 1366px) ${desktopWidth}, (min-width: 768px) 656px, calc(100vw - 96px)`;
}

function handleAction(action, index) {
  emit("action-click", { action, index });

  if (!action.url || action.disabled) return;

  if (action.target === "_blank") {
    window.open(action.url, "_blank", "noopener,noreferrer");
    return;
  }

  window.location.assign(action.url);
}
</script>

<style scoped lang="scss">
.ca-promo-module {
  width: 100%;
  padding: var(--size-8) 0;
  overflow: hidden;

  @include ca-bp("sm") {
    padding: var(--size-9) 0;
  }

  @include ca-bp("lg") {
    padding: var(--size-11) 0;
  }
}

.ca-promo-module__container {
  display: flex;
  justify-content: center;
}

.ca-promo-module__card {
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: var(--size-5);
  border-radius: var(--size-5);
  background:
    linear-gradient(
      0deg,
      var(--ca-color-backgrounds-container-high) 0%,
      var(--ca-color-backgrounds-container-high) 100%
    ),
    var(--ca-color-neutrals-inverted);

  @include ca-bp("md") {
    padding: var(--size-6);
    column-gap: var(--size-6) !important;
    border-radius: var(--size-6);
  }

  @include ca-bp("lg") {
    display: grid;
    align-items: stretch;
    column-gap: var(--size-10) !important;
    row-gap: 0;
    padding: var(--size-6) var(--size-7) var(--size-6) var(--size-6);
    border-radius: var(--size-8);
  }
}

// `ca-row` (DS) aplica display:grid em todos os breakpoints; em mobile queremos
// stacked em flex, mantendo apenas o gap-size-5 e sem o grid herdado.
@media (max-width: 767.98px) {
  .ca-promo-module__card.ca-row {
    display: flex !important;
  }
}

.ca-promo-module--container-light-green .ca-promo-module__card {
  background:
    linear-gradient(
      0deg,
      var(--ca-color-backgrounds-container-high) 0%,
      var(--ca-color-backgrounds-container-high) 100%
    ),
    var(--ca-color-neutrals-inverted);
}

.ca-promo-module--container-dark-green .ca-promo-module__card {
  background: var(--ca-color-hover-pressed-primary-pressed);
}

.ca-promo-module--container-gray .ca-promo-module__card {
  background: var(--ca-color-neutrals-on-background);
}

.ca-promo-module--container-none .ca-promo-module__card {
  background: none;
}

.ca-promo-module__media {
  --ca-promo-media-radius: var(--size-4);

  flex: 0 0 auto;

  @include ca-bp("sm") {
    --ca-promo-media-radius: var(--size-5);
  }

  @include ca-bp("lg") {
    --ca-promo-media-radius: var(--size-6);

    width: 100%;
    max-width: none;
  }
}

.ca-promo-module--size-s .ca-promo-module__media {
  min-height: 320px;
  height: auto;
}

.ca-promo-module--size-s .ca-promo-module__message {
  padding: var(--size-3) 0;

  @include ca-bp("sm") {
    padding: 0;
  }

  @include ca-bp("md") {
    padding: var(--size-4) 0;
  }
}

@include ca-bp("lg") {
  .ca-promo-module__media-parent {
    display: flex;
    align-self: stretch;
  }

  .ca-promo-module__media {
    flex: 1 1 auto;
    height: auto;
  }

  .ca-promo-module--size-m .ca-promo-module__card {
    min-height: 664px;
  }
}

@include ca-bp("md") {
  .ca-promo-module--size-s .ca-promo-module__card {
    display: grid;
    align-items: stretch;
    grid-template-columns: minmax(0, 1fr) 410px !important;
    column-gap: var(--size-8) !important;
    row-gap: 0;
    min-height: 384px;
    padding: var(--size-6);
  }

  .ca-promo-module--size-s.ca-promo-module--desktop-left
    .ca-promo-module__card {
    grid-template-columns: 410px minmax(0, 1fr) !important;
  }

  .ca-promo-module--size-s .ca-promo-module__message,
  .ca-promo-module--size-s .ca-promo-module__media-parent {
    grid-column: auto;
    grid-row: 1;
  }

  .ca-promo-module--size-s .ca-promo-module__media-parent {
    display: flex;
    align-self: stretch;
  }

  .ca-promo-module--size-s.ca-promo-module--desktop-right
    .ca-promo-module__message,
  .ca-promo-module--size-s.ca-promo-module--desktop-left
    .ca-promo-module__media-parent {
    grid-column: 1;
  }

  .ca-promo-module--size-s.ca-promo-module--desktop-right
    .ca-promo-module__media-parent,
  .ca-promo-module--size-s.ca-promo-module--desktop-left
    .ca-promo-module__message {
    grid-column: 2;
  }

  .ca-promo-module--size-s .ca-promo-module__media {
    flex: 0 0 410px;
    align-self: stretch;
    width: 410px;
    height: auto;
    max-width: 410px;
  }
}

.ca-promo-module--container-none .ca-promo-module__card {
  padding-inline: 0;
}

.ca-promo-module--stacked-bottom .ca-promo-module__media-parent {
  order: 1;
}

.ca-promo-module--stacked-top .ca-promo-module__media-parent {
  order: -1;
}

@include ca-bp("md") {
  .ca-promo-module--desktop-right .ca-promo-module__media-parent {
    order: 1;
  }

  .ca-promo-module--desktop-left .ca-promo-module__media-parent {
    order: -1;
  }
}

.ca-promo-module__actions {
  margin-top: var(--size-7);
}

/*
@include ca-bp("lg") {
  .ca-promo-module__actions {
    grid-area: actions;
    margin-top: calc(var(--size-7) - var(--size-4));
  }

  .ca-promo-module__media {
    grid-area: media;
    grid-row: 1 / -1;
    align-self: stretch;
    height: 100%;
  }

  .ca-promo-module--desktop-left .ca-promo-module__card {
    grid-template-areas:
      "media message"
      "media actions"
      "media .";
    grid-template-columns: minmax(0, 627.5fr) minmax(0, 514.5fr);
  }

  .ca-promo-module--desktop-right .ca-promo-module__card {
    grid-template-areas:
      "message media"
      "actions media"
      ". media";
    grid-template-columns: minmax(0, 514.5fr) minmax(0, 627.5fr);
  }

  .ca-promo-module--size-s.ca-promo-module--desktop-left
    .ca-promo-module__card {
    grid-template-columns: minmax(0, 410.5fr) minmax(0, 755.5fr);
  }

  .ca-promo-module--size-s.ca-promo-module--desktop-right
    .ca-promo-module__card {
    grid-template-columns: minmax(0, 755.5fr) minmax(0, 410.5fr);
  }
}
*/

.ca-promo-module__message,
.ca-promo-module__media-parent {
  padding: 0;
}

.ca-promo-module__message {
  @include ca-bp("lg") {
    padding: var(--size-4) 0;
  }
}

.ca-promo-module__module-message {
  align-items: flex-start;
  gap: var(--size-3);
  width: 100%;

  @include ca-bp("lg") {
    gap: var(--size-4);
  }

  :deep(.module-message__label) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    text-transform: uppercase;
    max-width: 100%;
    padding: var(--size-3) var(--size-4);
    border-radius: 100px;
    overflow-wrap: anywhere;
  }

  :deep(.module-message__text),
  :deep(.module-message__title),
  :deep(.module-message__description) {
    width: 100%;
  }

  :deep(.module-message__title),
  :deep(.module-message__description) {
    margin: 0;
    overflow-wrap: anywhere;
  }
}

.ca-promo-module--theme-default {
  .ca-promo-module__module-message {
    :deep(.module-message__title) {
      color: var(--ca-color-text-headers);
    }

    :deep(.module-message__description) {
      color: var(--ca-color-text-body-variant);
    }

    :deep(.module-message__label) {
      color: var(--ca-color-text-body);
      background-color: var(--ca-color-neutrals-on-background-low-10);
    }
  }
}

.ca-promo-module--theme-inverted {
  .ca-promo-module__module-message {
    :deep(.module-message__title) {
      color: var(--ca-color-text-inverted);
    }

    :deep(.module-message__description) {
      color: var(--ca-color-text-light);
    }

    :deep(.module-message__label) {
      color: var(--ca-color-text-inverted);
      background-color: var(--ca-color-neutrals-inverted-10);
    }
  }
}

.ca-promo-module__actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--size-4);

  button {
    width: fit-content;
    max-width: stretch;
  }
}

.ca-promo-module__action.base-button {
  height: auto;
  min-height: 48px;
  padding-top: var(--size-3);
  padding-bottom: var(--size-3);

  :deep(.button-text) {
    overflow: visible;
    text-align: center;
    text-overflow: clip;
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: normal;
  }
}

.ca-promo-module__actions--equal {
  display: flex;
  grid-auto-flow: row;
  grid-template-columns: fit-content(100%);
  justify-self: start;
  width: 100%;
  max-width: 100%;
  @include ca-bp("sm") {
    display: inline-grid;
  }
}

/* Icone do CTA vindo do media do Sitecore. Como e uma imagem e nao um icone do
   DS, nao herda a cor do botao — cabe ao autor carregar o ficheiro certo para a
   variante. Aqui so se garante que nao rebenta a altura do botao. */
.ca-promo-module__action-icon {
  display: block;
  width: 24px;
  height: 24px;
  object-fit: contain;
}

@include ca-bp("sm") {
  .ca-promo-module__actions--equal {
    display: flex;
    width: fit-content;
  }
}
</style>
